// Pelaajat (perhe): hahmokuvat ja pienet apufunktiot. Palvelin tuntee vain
// hahmon tunnuksen; kuva valitaan täällä, jotta kuvat voi vaihtaa ilman palvelinmuutosta.

/** Omistajan pelaajatunniste. Sama kuin backend/app/family.py:n OWNER. */
export const OWNER = "me";

export const EMBLEMS = [
  { id: "fox", emoji: "🦊", name: "Kettu" },
  { id: "bear", emoji: "🐻", name: "Karhu" },
  { id: "owl", emoji: "🦉", name: "Pöllö" },
  { id: "squirrel", emoji: "🐿️", name: "Orava" },
  { id: "deer", emoji: "🦌", name: "Peura" },
  { id: "frog", emoji: "🐸", name: "Sammakko" },
  { id: "hedgehog", emoji: "🦔", name: "Siili" },
  { id: "wolf", emoji: "🐺", name: "Susi" },
  { id: "bird", emoji: "🐦", name: "Lintu" },
  { id: "fish", emoji: "🐟", name: "Kala" },
];

export function emblemEmoji(id) {
  return EMBLEMS.find((e) => e.id === id)?.emoji ?? "🧭";
}

export function findPlayer(players, id) {
  return players?.find((p) => p.id === id) ?? null;
}

/** Retkelle valittavat kaverit: kaikki muut pelaajat kuin tallentaja. */
export function companionOptions(players, activeId) {
  return (players ?? []).filter((p) => p.id !== activeId);
}

/** Muistettu kaverivalinta: vain yhä olemassa olevat pelaajat, ei tallentajaa itseään. */
export function cleanCompanions(saved, players, activeId) {
  const ids = new Set(companionOptions(players, activeId).map((p) => p.id));
  return [...new Set(saved ?? [])].filter((id) => ids.has(id));
}

/** Nimet luettelona: "Aino", "Aino ja Eero", "Aino, Eero ja Liisa". */
export function joinNames(names) {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} ja ${names[names.length - 1]}`;
}

/**
 * Lähtöikkunan selitys kaverivalinnasta. Kertoo nimillä, kuka saa retkestä
 * pisteitä ja kuka ei, koska unohtunut valinta on tavallisin syy siihen, että
 * kaveri jää ilman: pelkkä "vain sinä" ei hoksauta, että kaverin voisi lisätä.
 * Palauttaa { alone, text }: alone on tosi, kun kukaan muu ei ole mukana.
 */
export function companionNote(options, chosenIds) {
  const chosen = options.filter((p) => chosenIds.includes(p.id)).map((p) => p.name);
  const left = options.filter((p) => !chosenIds.includes(p.id)).map((p) => p.name);
  if (!options.length) return { alone: true, text: "" };
  if (!chosen.length) {
    const verb = left.length === 1 ? "ei saa" : "eivät saa";
    const who = left.length === 1 ? "häntä" : "heitä";
    return { alone: true, text: `Vain sinä saat tästä retkestä ruudut ja pisteet. ${joinNames(left)} ${verb} mitään, ellet valitse ${who} mukaan.` };
  }
  const gets = chosen.length === 1 ? "saa" : "saavat";
  let text = `${joinNames(chosen)} ${gets} samat ruudut ja pisteet kuin sinä.`;
  if (left.length) text += ` ${joinNames(left)} ${left.length === 1 ? "ei ole" : "eivät ole"} mukana.`;
  return { alone: false, text };
}

/** "1 uusi ruutu", "3 uutta ruutua". */
export function newTilesText(n) {
  return n === 1 ? "1 uusi ruutu" : `${n} uutta ruutua`;
}

/**
 * Palvelimen "companions_earned" luettavaksi riveiksi, esim.
 * [{ name: "Aino", emblem: "bear", points: 30, text: "3 uutta ruutua" }].
 */
export function earnedLines(earned, players) {
  return Object.entries(earned ?? {}).map(([id, gain]) => {
    const player = findPlayer(players, id);
    return {
      id,
      name: player?.name ?? "Kaveri",
      emblem: player?.emblem,
      points: gain.points,
      text: gain.new_tiles ? newTilesText(gain.new_tiles) : "ei uusia ruutuja",
    };
  });
}

/**
 * Pelaajat, jotka tämä puhelin saa valita. Palvelimella kaikki pelaajat ovat samassa tietokannassa, joten
 * ilman rajausta kuka tahansa voisi vaihtaa toisen tunnukseen ja pelata hänenä. Tavallinen puhelin näkee
 * oman perheensä: pelaajat, jotka on luotu sillä (`mine`), ja heidän perheensä jäsenet (esim. lapset).
 * Jokaisella kaverilla voi olla oma perhe. Ylläpitäjän puhelin valitsee omistajan perheen (omistaja ja lapset).
 */
export function visiblePlayers(players, mine, admin) {
  if (!Array.isArray(players)) return players;
  const own = new Set(mine ?? []);
  // Ylläpitäjäkään ei valitse muiden perheitä: hänen omansa on omistajan perhe. Muiden pelaajia
  // hallitaan erikseen (otherPlayers), jotta heidän puolestaan ei voi pelata vahingossakaan.
  if (admin) own.add(OWNER);
  const families = new Set(players.filter((p) => own.has(p.id)).map((p) => p.family));
  return players.filter((p) => families.has(p.family));
}

/** Pelaajat, jotka ylläpitäjä näkee mutta ei voi valita pelaajaksi (muiden perheet). Tavalliselle puhelimelle tyhjä. */
export function otherPlayers(players, mine, admin) {
  if (!Array.isArray(players) || !admin) return [];
  const visible = new Set(visiblePlayers(players, mine, admin).map((p) => p.id));
  return players.filter((p) => !visible.has(p.id));
}

/**
 * Perheet valintalistaa varten: [{ id: perheen tunnus, members: [pelaaja], label: "Mika, Alicia" }],
 * omistajan perhe ensin (pelaajalistan järjestys säilyy).
 */
export function familyGroups(players) {
  const groups = new Map();
  for (const p of players ?? []) {
    if (!groups.has(p.family)) groups.set(p.family, { id: p.family, members: [] });
    groups.get(p.family).members.push(p);
  }
  return [...groups.values()].map((g) => ({ ...g, label: g.members.map((m) => m.name).join(", ") }));
}

/**
 * Pelaajan siirto perheestä toiseen (ylläpitäjä). `target` on perheen tunnus tai "new" (oma uusi perhe).
 * Palauttaa palvelimen pyynnön rungon tai null, kun siirrolle ei ole syytä (pelaaja on jo perheessä,
 * pelaajaa tai perhettä ei löydy) tai se ei ole sallittu (omistajaa ei siirretä).
 */
export function movePlan(players, playerId, target) {
  const player = (players ?? []).find((p) => p.id === playerId);
  if (!player || player.is_owner) return null;
  if (target === "new") return { new_family: true };
  const group = familyGroups(players).find((g) => g.id === target);
  if (!group || player.family === target) return null;
  return { join_with: group.members[0].id };
}

/**
 * Näytetäänkö ensikäynnistyksen "Kuka sinä olet?" -ikkuna. Uusi asennus alkaa aina omistajana,
 * joten ilman tätä kaveri pelaisi vahingossa omistajan tunnuksella. Ikkuna kysytään kerran per
 * asennus, kun palvelimella on jo useampi pelaaja. Ylläpitäjän puhelin ohittaa sen. Ikkuna tulee myös, jos puhelimen
 * pelaaja on poistettu palvelimelta (`activeFound` epätosi).
 */
export function needsWelcome(players, welcomed, admin = false, activeFound = true) {
  if (!Array.isArray(players) || admin) return false;
  // Valittu pelaaja on poistettu: kysytään uudelleen, ettei puhelin palaa pelaamaan omistajan tunnuksella.
  if (!activeFound) return true;
  return players.length >= 2 && !welcomed;
}

/**
 * Puhelimen tunnisteella löytyneet pelaajat (vanhin ensin) otetaan käyttöön ilman uutta pelaajaa.
 * Palauttaa { mine, adopt }: `mine` on päivitetty "tämän puhelimen pelaajat" -luettelo ja `adopt` pelaajan tunnus,
 * jolle vaihdetaan (tai null, kun nykyinen valinta on kunnossa). Vaihto tehdään, kun valittua pelaajaa ei ole
 * tai kukaan ei ole vielä kuitannut tervetuloikkunaa (tuore asennus näyttäisi muuten oletuksena omistajan).
 */
export function deviceClaimPlan(found, mine, activeId, activeValid, welcomed) {
  const ids = (found ?? []).map((p) => p.id);
  const merged = [...new Set([...(mine ?? []), ...ids])];
  // Nykyinen valinta on kunnossa: pelaaja on olemassa ja tervetuloikkuna on jo kuitattu.
  if (!ids.length || (activeValid && welcomed)) return { mine: merged, adopt: null };
  // Muuten jatketaan puhelimen oman pelaajan kanssa: nykyinen, jos se on yksi heistä, muuten vanhin.
  return { mine: merged, adopt: activeValid && ids.includes(activeId) ? activeId : ids[0] };
}
